import cloneDeep from "lodash.clonedeep";
import { ElementNotFoundError, findElement, findLayoutForElement, updatedLayoutForElement } from "./LayoutsState";
import { recomputeTextOnElement } from "./text/shared";
import {
    ColorDefinition,
    FontData,
    FrameElement,
    FrameOffsets,
    ILayout,
    IllustrationElement,
    ImageElement,
    LayoutElement,
    LayoutsState,
    LayoutState,
    PatternImageData,
    SerializableWorkflow,
    TextAlgorithm,
    TextboxElement,
    TextFillImage,
    TextFillSpotColor,
} from "./types";
import { generate } from "./util/guid";

/**
 * An abstract base class from which to extend all potential element commands. These commands
 * follow the command design pattern in software development. To learn more about this pattern
 * take a look at the following link.
 *
 * https://sourcemaking.com/design_patterns/command
 *
 */
export abstract class CanvasCommand {
    public abstract apply(state: LayoutsState): LayoutsState;

    // Undo/redo support.
    protected oldState?: LayoutsState;
    public undo(): LayoutsState {
        if (!this.oldState) {
            throw new Error("Cannot undo.");
        }
        return this.oldState;
    }
    public overrideOldState(state: LayoutsState) {
        this.oldState = state;
    }
    public sequenceId?: string;
}

/**
 * Sets the serializable workflow in the reducer state.
 */
export class UpdateWorkflowStateCommand extends CanvasCommand {
    private serializableWorkflow: SerializableWorkflow;

    constructor(serializableWorkflow: SerializableWorkflow) {
        super();
        this.serializableWorkflow = serializableWorkflow;
    }

    public apply(state: LayoutsState): LayoutsState {
        this.oldState = state;

        return {
            ...state,
            serializableWorkflow: this.serializableWorkflow,
        };
    }
}

/**
 * Shifts an element with given ID from its current position to a new position.
 */
export class MoveCommand extends CanvasCommand {
    private id: string;
    private x: number;
    private y: number;

    constructor(id: string, x: number, y: number) {
        super();
        this.id = id;
        this.x = x;
        this.y = y;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement = {
            ...el,
            x: this.x,
            y: this.y,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

/**
 * Rotates an element to a given angle in degrees.
 */
export class RotateCommand extends CanvasCommand {
    private id: string;
    private angle: number;

    constructor(id: string, angle: number) {
        super();
        this.id = id;
        this.angle = angle;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement = {
            ...el,
            rotation: this.angle,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

/**
 * Updates the width and height of an element to reflect a new size. Negative values will be
 * converted to their absolute value. ie. -10 will become 10.
 */
export class ResizeCommand extends CanvasCommand {
    private id: string;
    private width: number;
    private height: number;

    constructor(id: string, width: number, height: number) {
        super();
        this.id = id;
        this.width = Math.abs(width);
        this.height = Math.abs(height);
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        let updatedElement = {
            ...el,
            width: this.width,
            height: this.height,
        };
        if (el.type === "frame") {
            const frame = updatedElement as FrameElement;
            frame.scaleX = (frame.scaleX * this.width) / el.width;
            frame.scaleY = (frame.scaleY * this.height) / el.height;
        } else if (el.type === "textbox") {
            const textbox = updatedElement as TextboxElement;
            const text =
                (!textbox.algorithm || textbox.algorithm === TextAlgorithm.Autosize ? textbox.text : textbox.input) ??
                "";
            updatedElement = recomputeTextOnElement(textbox, text, el as TextboxElement);
        }

        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

/**
 * Applys a list of command objects to the current state, the final state
 * will be that of all commands applied in the order of left to right.
 */
export class GroupCommand extends CanvasCommand {
    private commands: CanvasCommand[];
    constructor(commands: CanvasCommand[]) {
        super();
        this.commands = commands;
    }
    public apply(state: LayoutsState): LayoutsState {
        this.oldState = state;

        return this.commands.reduce<LayoutsState>((accumulatedState, curCommand) => {
            return curCommand.apply(accumulatedState);
        }, state);
    }
}

/*
 * Creates a new layout with a given layout from the server.
 * in layout array. Ensure to write a test for this as well.
 */
export class CreateLayoutCommand extends CanvasCommand {
    private layout: ILayout;

    constructor(layout: ILayout) {
        super();
        this.layout = layout;
    }

    public apply(state: LayoutsState): LayoutsState {
        this.oldState = state;

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [this.layout.id]: {
                    elements: [],
                    layout: { ...this.layout },
                    modificationID: generate(),
                },
            },
        };
    }
}

/**
 * Clear all elements from a given layout.
 */
export class ClearLayoutCommand extends CanvasCommand {
    private panelName: string;

    constructor(panelName: string) {
        super();
        this.panelName = panelName;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const layouts = Object.values(state.layouts).map((layout) => {
            const targetLayout = layout.layout.panelId === this.panelName;
            const state: LayoutState = {
                ...layout,
                elements: targetLayout ? [] : [...layout.elements],
                modificationID: generate(),
            };
            return state;
        });
        const finalLayouts: { [key: string]: LayoutState } = {};
        layouts.forEach((layoutState) => {
            finalLayouts[layoutState.layout.id] = layoutState;
        });
        return {
            ...state,
            layouts: finalLayouts,
        };
    }
}

/**
 * Add an element to the canvas
 */
export class CreateElementCommand<T extends LayoutElement> extends CanvasCommand {
    private element: T;
    private layout: ILayout;

    /**
     * @param initialParams The initial parameters to be set on this new object
     * @param callback An optional callback function to be notified when the object has been instantiated
     * @param layout
     */
    constructor(element: T, layout: ILayout) {
        super();
        this.element = element;
        this.layout = layout;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        if (!state.layouts[this.layout.id]) {
            state.layouts[this.layout.id] = { layout: this.layout, elements: [], modificationID: "" };
        }

        const currentElementsInLayout = state.layouts[this.layout.id].elements;
        const duplicateElementAlreadyExists = currentElementsInLayout.find((el) => el.id === this.element.id);
        if (duplicateElementAlreadyExists) {
            throw new Error(
                `Failed to apply new ${this.element.type} element because ID ${this.element.id} already taken`,
            );
        }

        if (this.element.productOverlay || currentElementsInLayout.length === 0) {
            const newElements = [...currentElementsInLayout, this.element];
            if (this.element.layerIndex === undefined) {
                this.element.layerIndex = this.assignIndex(this.element, newElements);
            }
            return {
                ...state,
                layouts: {
                    ...state.layouts,
                    [this.layout.id]: {
                        ...state.layouts[this.layout.id],
                        elements: newElements,
                        modificationID: generate(),
                    },
                },
            };
        }

        // If product overlay exists then it is the final element.
        const finalElement = currentElementsInLayout[currentElementsInLayout.length - 1];
        const productOverlay = finalElement.productOverlay ? finalElement : null;

        // Reindex elements.
        if (productOverlay) {
            currentElementsInLayout.pop();
        }
        let updatedLayoutsForElement: LayoutElement[];
        if (this.element.layerIndex !== null && this.element.layerIndex !== undefined) {
            updatedLayoutsForElement = [...currentElementsInLayout, this.element].sort((a, b) => {
                if (a.layerIndex !== undefined && b.layerIndex !== undefined) {
                    return a.layerIndex - b.layerIndex;
                }
                return 0;
            });
        } else {
            const newIndex = this.assignIndex(this.element, currentElementsInLayout);
            this.element.layerIndex = newIndex !== undefined ? newIndex : 0;
            updatedLayoutsForElement = [...currentElementsInLayout, this.element];
        }
        if (productOverlay) {
            updatedLayoutsForElement.push(productOverlay);
        }

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [this.layout.id]: {
                    ...state.layouts[this.layout.id],
                    elements: updatedLayoutsForElement,
                    modificationID: generate(),
                },
            },
        };
    }

    // Calculate layer index for an element that doesn't have an explicit index.
    private assignIndex(target: LayoutElement, elementArray: LayoutElement[]): number {
        if (elementArray.length === 0) {
            return 0;
        }
        // We find the highest index used on the layer of the target element.
        const lastIndexOfLayer = elementArray
            .filter((el) => (el.layer || 0) === (target.layer || 0))
            .sort((a, b) => (a.layerIndex || 0) - (b.layerIndex || 0))[elementArray.length - 1]?.layerIndex;

        return lastIndexOfLayer !== undefined ? lastIndexOfLayer + 1 : 0;
    }
}

/**
 * Delete an element on the canvas
 */
export class DeleteElementCommand extends CanvasCommand {
    private id: string;

    constructor(id: string) {
        super();
        this.id = id;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        let elementFound = false;
        const filteredLayouts = Object.values(state.layouts).map((layout) => {
            const filteredElements = layout.elements.filter((el) => el.id !== this.id);
            if (filteredElements.length !== layout.elements.length) {
                elementFound = true;
            }
            return {
                ...layout,
                elements: layout.elements.filter((el) => {
                    return el.id !== this.id;
                }),
                modificationID: generate(),
            };
        });
        if (!elementFound) {
            console.log(`Failed to delete element ${this.id}`);
        }
        const finalLayouts: { [key: string]: LayoutState } = {};
        filteredLayouts.forEach((layoutState) => {
            finalLayouts[layoutState.layout.id] = layoutState;
        });
        return {
            ...state,
            layouts: finalLayouts,
        };
    }
}

export class CloneElementCommand extends CanvasCommand {
    private el: LayoutElement;
    private layout: ILayout;

    constructor(el: LayoutElement, layout: ILayout) {
        super();
        this.el = cloneDeep(el);
        this.layout = layout;
        this.el.id = generate();
        this.el.x += 5;
        this.el.y += 5;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        return new CreateElementCommand(this.el, this.layout).apply(state);
    }
}

export class FontColorCommand extends CanvasCommand {
    private id: string;
    private color: string;
    private textFillSpotColor?: TextFillSpotColor;

    constructor(id: string, color: string, textFillSpotColor?: TextFillSpotColor) {
        super();
        this.id = id;
        this.color = color;
        this.textFillSpotColor = textFillSpotColor;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }

        const updatedElement = {
            ...el,
            fill: this.color,
            fillSpotColorDefinition: this.textFillSpotColor,
            textFillImage: undefined,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class FontImageFillCommand extends CanvasCommand {
    private id: string;
    private imageFill: TextFillImage;

    constructor(id: string, imageFill: TextFillImage) {
        super();
        this.id = id;
        this.imageFill = imageFill;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }

        const updatedElement = {
            ...el,
            textFillImage: this.imageFill,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class FontSizeCommand extends CanvasCommand {
    private id: string;
    private size: number;

    constructor(id: string, size: number) {
        super();
        this.id = id;
        this.size = size;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement: TextboxElement = {
            ...el,
            fontSize: this.size,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        // FIXME: The autosize algorithm injects newlines outside of the commands, we need
        // to ignore the input text in that case. See getCalculatedMixins within recomputeTextOnElement
        // The logic for autosize should be shifted into there in the future.
        const text = (!el.algorithm || el.algorithm === TextAlgorithm.Autosize ? el.text : el.input) ?? "";
        const updatedLayout = updatedLayoutForElement(recomputeTextOnElement(updatedElement, text), layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class FontAlgorithmCommand extends CanvasCommand {
    private id: string;
    private algorithm: TextAlgorithm;

    constructor(id: string, algorithm: TextAlgorithm) {
        super();
        this.id = id;
        this.algorithm = algorithm;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement = {
            ...el,
            algorithm: this.algorithm,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class FontSourceCommand extends CanvasCommand {
    private id: string;
    private fontData: FontData;

    constructor(id: string, fontData: FontData) {
        super();
        this.id = id;
        this.fontData = fontData;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement: TextboxElement = {
            ...el,
            fontData: this.fontData,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const text = (!el.algorithm || el.algorithm === TextAlgorithm.Autosize ? el.text : el.input) ?? "";
        const updatedLayout = updatedLayoutForElement(recomputeTextOnElement(updatedElement, text), layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class FontAlignmentCommand extends CanvasCommand {
    private id: string;
    private align: "left" | "center" | "right";

    constructor(id: string, align: "left" | "center" | "right") {
        super();
        this.id = id;
        this.align = align;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement: TextboxElement = {
            ...el,
            align: this.align,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class UpdateFramePattern extends CanvasCommand {
    private id: string;
    private imageData: PatternImageData;
    private offsets: FrameOffsets;

    constructor(id: string, imageData: PatternImageData, offsets: FrameOffsets) {
        super();
        this.id = id;
        this.imageData = imageData;
        this.offsets = offsets;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const targetLayout = findLayoutForElement(this.id, Object.values(state.layouts));
        const index = targetLayout.elements.findIndex((el) => el.id === this.id);
        const existingPattern = (targetLayout.elements[index] as FrameElement).pattern;

        const newElements = [...targetLayout.elements];
        newElements.splice(index, 1, {
            ...targetLayout.elements[index],
            pattern: {
                ...existingPattern,
                src: this.imageData.src,
                x: this.offsets.x,
                y: this.offsets.y,
                width: this.imageData.width,
                height: this.imageData.height,
                scaleX: this.offsets.zoom,
                scaleY: this.offsets.zoom,
                svg: this.imageData.svg,
                colors: this.imageData.colors,
            },
        } as FrameElement); // FIXME: How can we assign LayoutElement correctly?

        const newLayout: LayoutState = {
            ...targetLayout,
            elements: newElements,
            modificationID: generate(),
        };

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [targetLayout.layout.id]: newLayout,
            },
        };
    }
}

export class UpdateFrameThresholdSettingsCommand extends CanvasCommand {
    private id: string;
    private useThreshold: boolean;
    private invertThreshold: boolean;
    private threshold: number;
    private thresholdSaturation: number;

    constructor(
        id: string,
        useThreshold: boolean,
        invertThreshold: boolean,
        threshold: number,
        thresholdSaturation: number,
    ) {
        super();
        this.id = id;
        this.useThreshold = useThreshold;
        this.invertThreshold = invertThreshold;
        this.threshold = threshold;
        this.thresholdSaturation = thresholdSaturation;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;
        const el = findElement(this.id, Object.values(state.layouts)) as FrameElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }

        const updatedElement = {
            ...el,
            useThreshold: this.useThreshold,
            invertThreshold: this.invertThreshold,
            threshold: this.threshold,
            thresholdSaturation: this.thresholdSaturation,
        };

        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class UpdateImageSourceCommand extends CanvasCommand {
    private id: string;
    private src: string;

    constructor(id: string, src: string) {
        super();
        this.id = id;
        this.src = src;
    }

    public apply(state: LayoutsState): LayoutsState {
        this.oldState = state;
        const el = findElement(this.id, Object.values(state.layouts)) as ImageElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement: ImageElement = {
            ...el,
            src: this.src,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

/**
 * TextChange will modify the text displayed in a text box
 */
export class TextChangeCommand extends CanvasCommand {
    private id: string;
    private text: string;

    constructor(id: string, text: string) {
        super();
        this.id = id;
        this.text = text;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;
        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(recomputeTextOnElement(el, this.text, el), layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class TextStrokeCommand extends CanvasCommand {
    private id: string;
    private strokeColor: ColorDefinition | undefined;
    private strokeThickness: string | undefined;

    constructor(id: string, strokeColor: ColorDefinition | undefined, strokeThickness: string | undefined) {
        super();
        this.id = id;
        this.strokeColor = strokeColor;
        this.strokeThickness = strokeThickness;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;
        const el = findElement(this.id, Object.values(state.layouts)) as TextboxElement;
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const updatedElement = {
            ...el,
            strokeColor: this.strokeColor,
            strokeThickness: this.strokeThickness,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class IllustrationColorCommand extends CanvasCommand {
    private id: string;
    private className: string;
    private fill: string | ColorDefinition;

    constructor(id: string, className: string, fill: string | ColorDefinition) {
        super();
        this.id = id;
        this.className = className;
        this.fill = fill;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const newColors = (el as IllustrationElement).colors || {};
        const oldColor = newColors[this.className];

        if (typeof this.fill === "string") {
            newColors[this.className] = {
                browserValue: this.fill,
                spotColor: oldColor?.spotColor,
                pmsValue: oldColor?.pmsValue,
            };
        } else {
            newColors[this.className] = {
                browserValue: this.fill.browserValue,
                spotColor: oldColor?.spotColor,
                pmsValue: this.fill.pmsValue,
            };
        }

        const updatedElement = {
            ...el,
            colors: newColors,
        };
        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class IllustrationCacheCommand extends CanvasCommand {
    private id: string;
    private svgBody: string;
    private objectURL: string;

    constructor(id: string, svgBody: string, objectURL: string) {
        super();
        this.id = id;
        this.svgBody = svgBody;
        this.objectURL = objectURL;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }

        const updatedElement = {
            ...el,
            svg: this.svgBody,
            cachedObjectURL: this.objectURL,
        };

        const layout = findLayoutForElement(el.id, Object.values(state.layouts));
        const updatedLayout = updatedLayoutForElement(updatedElement, layout);
        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayout,
            },
        };
    }
}

export class BringToFrontCommand extends CanvasCommand {
    private id: string;

    /**
     * @param id The targeted element to bring to the front.
     */
    constructor(id: string) {
        super();
        this.id = id;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const layout = Object.values(state.layouts).find((l) => l.elements.find((el) => el.id === this.id));
        if (!layout) {
            throw new Error("Layout missing from state!");
        }

        // We shift the element in front of the last index and then reorganise the layer indices
        // to suite the new ordering of the element array.
        const fromIndex = layout.elements.findIndex((el) => el.id === this.id);
        layout.elements.push(layout.elements.splice(fromIndex, 1)[0]);

        const updatedElements = [...layout.elements];
        updatedElements.forEach((el, idx) => (el.layerIndex = idx));

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: {
                    ...state.layouts[layout.layout.id],
                    elements: updatedElements,
                    modificationID: generate(),
                },
            },
        };
    }
}

export class BringToBackCommand extends CanvasCommand {
    private id: string;

    /**
     * @param id The targeted element to bring to the back.
     */
    constructor(id: string) {
        super();
        this.id = id;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const layout = Object.values(state.layouts).find((l) => l.elements.find((el) => el.id === this.id));
        if (!layout) {
            throw new Error("Layout missing from state!");
        }

        // We shift the element backwards and then reorganise the layer indices
        // to suite the new ordering of the element array.
        const fromIndex = layout.elements.findIndex((el) => el.id === this.id);

        layout.elements.splice(fromIndex, 1);
        layout.elements.unshift(el);

        const updatedElements = [...layout.elements];
        const element = updatedElements.splice(fromIndex, 1)[0];
        updatedElements.splice(fromIndex, 0, element);
        updatedElements.forEach((el, idx) => (el.layerIndex = idx));

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: {
                    ...state.layouts[layout.layout.id],
                    elements: updatedElements,
                    modificationID: generate(),
                },
            },
        };
    }
}

/**
 * Bring an element forward by one layer. Does nothing if the element is already at the front.
 */
export class BringForwardCommand extends CanvasCommand {
    private id: string;

    /**
     * @param id The targeted element to bring to the front.
     */
    constructor(id: string) {
        super();
        this.id = id;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const layout = Object.values(state.layouts).find((l) => l.elements.find((el) => el.id === this.id));
        if (!layout) {
            throw new Error("Layout missing from state!");
        }

        // We shift the element forward and then reorganise the layer indices
        // to suite the new ordering of the element array.
        const fromIndex = layout.elements.findIndex((el) => el.id === this.id);
        const toIndex = fromIndex + 1;
        const updatedElements = [...layout.elements];
        const element = updatedElements.splice(fromIndex, 1)[0];
        updatedElements.splice(toIndex, 0, element);
        updatedElements.forEach((el, idx) => (el.layerIndex = idx));

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: {
                    ...state.layouts[layout.layout.id],
                    elements: updatedElements,
                    modificationID: generate(),
                },
            },
        };
    }
}

/**
 * Send an element backwards by one layer. Does nothing if the element is already at the back.
 */
export class SendBackwardsCommand extends CanvasCommand {
    private id: string;

    /**
     * @param id The targeted element to bring to the front.
     */
    constructor(id: string) {
        super();
        this.id = id;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }
        const layout = Object.values(state.layouts).find((l) => l.elements.find((el) => el.id === this.id));
        if (!layout) {
            throw new Error("Layout missing from state!");
        }

        // We shift the element backwards and then reorganise the layer indices
        // to suite the new ordering of the element array.
        const fromIndex = layout.elements.findIndex((el) => el.id === this.id);
        const toIndex = fromIndex - 1;
        const updatedElements = [...layout.elements];
        const element = updatedElements.splice(fromIndex, 1)[0];
        updatedElements.splice(toIndex, 0, element);
        updatedElements.forEach((el, idx) => (el.layerIndex = idx));

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: {
                    ...state.layouts[layout.layout.id],
                    elements: updatedElements,
                    modificationID: generate(),
                },
            },
        };
    }
}

/**
 * Set the layer value of an element. The layer is half of the system involved with
 * determining order.
 */
export class LayerCommand extends CanvasCommand {
    private id: string;
    private value: number;

    /**
     * @param id The targeted element to change layer of.
     * @param value The new layer to set this element within.
     */
    constructor(id: string, value: number) {
        super();
        this.id = id;
        this.value = value;
    }

    public apply(state: LayoutsState) {
        this.oldState = state;

        const el = findElement(this.id, Object.values(state.layouts));
        if (!el.id) {
            throw new ElementNotFoundError();
        }

        const layout = Object.values(state.layouts).find((l) => l.elements.find((el) => el.id === this.id));
        if (!layout) {
            throw new Error("Layout missing from state!");
        }

        return {
            ...state,
            layouts: {
                ...state.layouts,
                [layout.layout.id]: updatedLayoutForElement({ ...el, layer: this.value }, layout),
            },
        };
    }
}
